"use client";

/**
 * AccessLevelPicker — the "Assistant Access" dial (permissions v2, spec §1/§4).
 *
 * A Vellum-style dropdown of the four human access levels. Two placements share
 * it: the settings page (global default) and the task header/composer (per-task
 * override). While inheriting, the closed trigger shows just the level (no
 * "(default)" tag — founder call); the open dropdown leaves no row checked.
 *
 * Presentational only: it renders `value` and calls `onChange` — the placements
 * own the persistence (settings API vs SET_ACCESS_LEVEL socket). Level copy is
 * the founder-approved wording from spec §1; do not improvise it.
 *
 * A null `value` means we do NOT know the level: the read is in flight, or it
 * failed. The trigger then says so ("Loading" / "Not loaded") instead of naming
 * a level. Never fill a null with a guess — a fabricated level reads exactly
 * like a setting the user chose, and it reads LOOSER than the posture the server
 * is actually enforcing.
 */

import { useState } from "react";
import type { AccessLevel } from "@bash/shared";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { AccessLevelGlyph } from "@/components/icons/AccessLevelGlyph";
import { ChevronToggleIcon } from "@/components/icons/ChevronToggleIcon";
import { CheckIcon } from "@/components/icons/CheckIcon";

interface LevelConfig {
  value: AccessLevel;
  label: string;
  blurb: string;
}

/** Founder-approved level copy (spec §1). No em dashes; never the word "Claude". */
const LEVELS: readonly LevelConfig[] = [
  {
    value: "careful",
    label: "Careful",
    blurb: "Asks before anything that makes a change",
  },
  {
    value: "balanced",
    label: "Balanced",
    blurb:
      "Works freely in your workspace, asks before acting on the outside world",
  },
  {
    value: "relaxed",
    label: "Relaxed",
    blurb: "Asks only for payments and deletions. The default",
  },
  {
    value: "autopilot",
    label: "Autopilot",
    blurb: "Runs everything on its own. Payments still need your OK",
  },
];

interface AccessLevelPickerProps {
  /** The effective level to display and highlight, or null when we do not know
   *  it (read in flight, or the read failed — see `loadFailed`). Callers must
   *  pass null rather than substituting a level they have not read. */
  value: AccessLevel | null;
  onChange: (level: AccessLevel) => void;
  /** Read while `value` is null: the read FAILED rather than being in flight.
   *  Swaps the quiet "Loading" trigger for "Not loaded" and opens the Try again
   *  row in the panel. */
  loadFailed?: boolean;
  /** Re-run the failed read. Renders the panel's "Try again" affordance. */
  onRetry?: () => void;
  /** Task placement: `value` is inherited from the user default (no explicit
   *  override). No longer shown on the closed trigger; still suppresses the
   *  active-row check in the open dropdown. */
  inheriting?: boolean;
  /** A custom access config is active (B2): trigger shows "Custom", no preset row
   * shows the active check, and the panel footer explains how to reset. Derived by
   * the caller (`accessConfig != null`) — never stored as a fifth level. */
  custom?: boolean;
  disabled?: boolean;
  /** Icon-only trigger for a squeezed composer (the Computer panel is open):
   *  the level glyph carries the pill; label + chevron return when space does.
   *  The aria-label/title keep the full meaning. Dropdown unchanged. */
  compact?: boolean;
  /** Collapse to icon-only on mobile widths (< md). The composer placements set
   *  this; the settings page keeps the full label at every width. */
  collapseOnMobile?: boolean;
  className?: string;
}

export function AccessLevelPicker({
  value,
  onChange,
  loadFailed,
  onRetry,
  inheriting,
  custom,
  disabled,
  compact,
  collapseOnMobile,
  className,
}: AccessLevelPickerProps) {
  const [open, setOpen] = useState(false);

  // No `?? LEVELS[2]` fallback: an unknown value used to render as Relaxed,
  // which is the same fabrication this control exists to avoid.
  const current = LEVELS.find((l) => l.value === value) ?? null;
  const unknown = current == null && !custom;
  const triggerLabel = custom
    ? "Custom"
    : current
      ? current.label
      : loadFailed
        ? "Not loaded"
        : "Loading";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="icon"
                size="xxs-icon"
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={`Assistant Access: ${triggerLabel}`}
                className={cn("gap-2 disabled:opacity-90", className)}
              >
                <AccessLevelGlyph
                  kind={custom ? "custom" : current ? current.value : "unknown"}
                  width={15}
                  height={15}
                />
                {!compact && (
                  <>
                    {/* --fg-subtle, never --fg-faint: on the dark chip (--bg-muted #111115)
                  faint is #3a3a42, ~1.67:1, which erases the one word this state
                  exists to show. Quieter than a known level, still legible. */}
                    <span
                      className={cn(
                        collapseOnMobile && "sr-only md:not-sr-only",
                        unknown && "text-fg-subtle"
                      )}
                    >
                      {triggerLabel}
                    </span>
                    <ChevronToggleIcon
                      open={open}
                      className={cn(
                        "text-fg-faint",
                        collapseOnMobile && "hidden md:inline"
                      )}
                    />
                  </>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={6}
            className={cn(open && "hidden")}
          >
            {`Assistant Access: ${triggerLabel}`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={16}
        className="z-[80] p-0 w-90 max-w-[calc(100vw-32px)] overflow-hidden"
      >
        <div role="listbox" aria-label="Assistant Access">
          <div className="px-5 pt-4 pb-2 text-3 font-semibold uppercase tracking-wider text-fg-faint">
            Assistant Access
          </div>
          {LEVELS.map((l) => {
            const isActive = l.value === value && !inheriting && !custom;
            return (
              <button
                type="button"
                key={l.value}
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(l.value);
                  setOpen(false);
                }}
                className={cn(
                  "w-full text-left flex items-start gap-3 px-5 py-3 border-l-[3px] transition-all duration-100",
                  isActive
                    ? "bg-bg-muted border-l-fg-default"
                    : "border-l-transparent hover:bg-bg-muted"
                )}
              >
                <span className="mt-0.5 text-fg-muted" aria-hidden>
                  <AccessLevelGlyph kind={l.value} width={17} height={17} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2 text-3.5 font-semibold text-fg-default">
                    {l.label}
                    {isActive && (
                      <CheckIcon
                        width={13}
                        height={13}
                        strokeWidth="3"
                        className="text-fg-muted"
                        aria-hidden
                      />
                    )}
                  </span>
                  <span className="block text-3 leading-[1.5] mt-0.5 text-fg-muted">
                    {l.blurb}
                  </span>
                </span>
              </button>
            );
          })}
          {custom && (
            <div className="px-5 py-2.5 text-3 text-fg-faint border-t border-[var(--border-subtle,var(--border-default))]">
              Customized. Pick a level to clear your custom settings
            </div>
          )}
          {unknown && loadFailed && (
            <div className="px-5 py-2 flex items-center justify-between gap-3 border-t border-[var(--border-subtle,var(--border-default))]">
              <span className="text-3 text-fg-subtle">
                Could not load your current setting
              </span>
              {onRetry && (
                <button
                  type="button"
                  onClick={() => {
                    // Close first: the footer is about to swap to the quiet
                    // in-flight label, and a control that vanishes under the
                    // cursor reads as a dead click.
                    setOpen(false);
                    onRetry();
                  }}
                  className="shrink-0 px-2 py-1 rounded-8 text-3 font-medium text-fg-muted hover:bg-bg-muted transition-colors"
                >
                  Try again
                </button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
